// Moteur d'export des représentations d'arbre.
//   - exportSvgToPdf : PDF VECTORIEL via svg2pdf.js (net au zoom, texte
//     sélectionnable). Fonctionne car nos charts sont en SVG natif (pas de
//     foreignObject, que svg2pdf ne sait pas rendre).
//   - exportSvgToPng : PNG haute résolution (sérialisation SVG → canvas).
//   - exportRasterToPdf : repli non vectoriel pour la vue « cartes » existante
//     (foreignObject) déjà capturée en image.
// Imports dynamiques (comme jspdf l'est déjà dans FamilyTree) pour ne pas
// alourdir le bundle initial.

const SVGNS = 'http://www.w3.org/2000/svg';

// Dimensions logiques d'un <svg> (attributs width/height, sinon viewBox, sinon rendu).
function svgSize(svgEl: SVGSVGElement): { w: number; h: number } {
  const wAttr = Number(svgEl.getAttribute('width'));
  const hAttr = Number(svgEl.getAttribute('height'));
  if (wAttr && hAttr) return { w: wAttr, h: hAttr };
  const vb = svgEl.viewBox?.baseVal;
  if (vb && vb.width && vb.height) return { w: vb.width, h: vb.height };
  const r = svgEl.getBoundingClientRect();
  return { w: r.width || 1, h: r.height || 1 };
}

function triggerDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

// Sérialise un <svg> en data-URL image/svg+xml.
function serializeSvg(svgEl: SVGSVGElement): string {
  const clone = svgEl.cloneNode(true) as SVGSVGElement;
  clone.setAttribute('xmlns', SVGNS);
  const xml = new XMLSerializer().serializeToString(clone);
  return 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(xml);
}

/**
 * Export PDF vectoriel d'un élément <svg> natif. Le PDF est dimensionné
 * exactement au dessin (page = taille du SVG en points), texte sélectionnable.
 */
export async function exportSvgToPdf(svgEl: SVGSVGElement, filename: string): Promise<void> {
  const { jsPDF } = await import('jspdf');
  await import('svg2pdf.js'); // patche jsPDF.prototype.svg
  const { w, h } = svgSize(svgEl);
  const doc = new jsPDF({ orientation: 'portrait', unit: 'pt', format: [w, h] });
  // .svg() est ajouté par svg2pdf.js (non typé sur l'import dynamique).
  await (doc as unknown as { svg: (el: Element, o: object) => Promise<void> })
    .svg(svgEl, { x: 0, y: 0, width: w, height: h });
  doc.save(filename);
}

/**
 * Export PNG haute résolution (facteur `scale`) d'un élément <svg> natif.
 */
export async function exportSvgToPng(svgEl: SVGSVGElement, filename: string, scale = 2): Promise<void> {
  const { w, h } = svgSize(svgEl);
  const url = serializeSvg(svgEl);
  const canvas = document.createElement('canvas');
  canvas.width = Math.ceil(w * scale);
  canvas.height = Math.ceil(h * scale);
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('canvas indisponible');

  await new Promise<void>((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      ctx.setTransform(scale, 0, 0, scale, 0, 0);
      ctx.drawImage(img, 0, 0);
      resolve();
    };
    img.onerror = () => reject(new Error('rendu SVG échoué'));
    img.src = url;
  });

  await new Promise<void>((resolve, reject) => {
    canvas.toBlob(blob => {
      if (!blob) { reject(new Error('encodage PNG échoué')); return; }
      triggerDownload(blob, filename);
      resolve();
    }, 'image/png');
  });
}

/**
 * Repli non vectoriel : place une image déjà rasterisée (vue « cartes ») dans
 * un PDF dimensionné à l'image.
 */
export async function exportRasterToPdf(
  dataUrl: string, width: number, height: number, filename: string,
): Promise<void> {
  const { jsPDF } = await import('jspdf');
  const doc = new jsPDF({ orientation: width >= height ? 'landscape' : 'portrait', unit: 'px', format: [width, height] });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  doc.addImage(dataUrl, 'PNG', 0, 0, pageW, pageH);
  doc.save(filename);
}

// Télécharge une data-URL PNG telle quelle (vue « cartes » en PNG).
export function downloadDataUrl(dataUrl: string, filename: string): void {
  const a = document.createElement('a');
  a.href = dataUrl;
  a.download = filename;
  a.click();
}
